import { normalizeAlias } from '../normalize/index.ts';
import { activeLocations, type ConfigIndex, type LocationEntry } from './loader.ts';

/**
 * D-002 / F-002 — location search.
 *
 * One implementation for the Worker's config index and for the browser's
 * bootstrap payload. Participants type on a phone keyboard at a start line:
 * requiring diacritics, or the exact post-merger province name, would fail
 * most of them, so code, name, province and aliases are all matched with
 * diacritics stripped.
 */

/** The shape both `LocationEntry` and the public bootstrap location satisfy. */
export interface SearchableLocation {
  code: string;
  name: string;
  province?: string | null;
  aliases: readonly string[];
}

export function locationSearchTokens(location: SearchableLocation): string[] {
  const raw = [location.code, location.name, location.province ?? '', ...location.aliases];
  const tokens = raw.map(normalizeAlias).filter((token) => token.length > 0);
  return [...new Set(tokens)];
}

export function matchesQuery(location: SearchableLocation, normalizedQuery: string): boolean {
  if (normalizedQuery.length === 0) return true;
  return locationSearchTokens(location).some((token) => token.includes(normalizedQuery));
}

/** Filter any list of locations by a raw, user-typed query. */
export function filterLocations<T extends SearchableLocation>(
  locations: readonly T[],
  query: string,
): T[] {
  const normalizedQuery = normalizeAlias(query);
  if (normalizedQuery.length === 0) return [...locations];
  return locations.filter((location) => matchesQuery(location, normalizedQuery));
}

/** Active locations of one event matching the query, in display order. */
export function searchLocations(
  index: ConfigIndex,
  eventId: string,
  query: string,
): readonly LocationEntry[] {
  return filterLocations(activeLocations(index, eventId), query);
}
